import React, { useState, useEffect, useCallback } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Gauge, Thermometer, SprayCan, Bug, Wrench, Truck,
  AlertTriangle, CheckCircle2, XCircle, ArrowRight,
  RefreshCw, Shield, AlertCircle, TrendingUp, TrendingDown,
  Brain, Loader2, ChevronDown, ChevronUp, Zap,
  Clock, CalendarDays, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import api from "@/api/client";

const MODULE_META = {
  temperature:   { label: "Temperatures",    icon: Thermometer, page: "Temperatures",  color: "text-orange-600", bg: "bg-orange-50" },
  cleaning:      { label: "Neteja",          icon: SprayCan,    page: "Cleaning",      color: "text-blue-600",   bg: "bg-blue-50" },
  pest_control:  { label: "Control Plagues", icon: Bug,         page: "PestControl",   color: "text-red-600",    bg: "bg-red-50" },
  maintenance:   { label: "Manteniment",     icon: Wrench,      page: "Maintenance",   color: "text-purple-600", bg: "bg-purple-50" },
  traceability:  { label: "Traçabilitat",    icon: Truck,       page: "Traceability",  color: "text-teal-600",   bg: "bg-teal-50" },
  incidents:     { label: "Incidències",     icon: AlertTriangle, page: "Incidents",   color: "text-amber-600",  bg: "bg-amber-50" },
};

const LEVEL_CONFIG = {
  minimal:  { label: "Excel·lent",  color: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-200", progress: "bg-emerald-500" },
  low:      { label: "Bo",          color: "text-green-700",   bg: "bg-green-50",    border: "border-green-200",   progress: "bg-green-500" },
  medium:   { label: "Mitjà",       color: "text-amber-700",   bg: "bg-amber-50",    border: "border-amber-200",   progress: "bg-amber-500" },
  high:     { label: "Alt",         color: "text-orange-700",  bg: "bg-orange-50",   border: "border-orange-200",  progress: "bg-orange-500" },
  critical: { label: "Crític",      color: "text-red-700",     bg: "bg-red-50",      border: "border-red-200",     progress: "bg-red-500" },
};

const SEV_ICON = { critical: XCircle, high: AlertTriangle, medium: AlertCircle, low: CheckCircle2 };
const SEV_STYLE = {
  critical: "bg-red-50 border-red-200 text-red-800",
  high: "bg-orange-50 border-orange-200 text-orange-800",
  medium: "bg-amber-50 border-amber-200 text-amber-800",
  low: "bg-blue-50 border-blue-200 text-blue-700",
};

const PATTERN_ICON = {
  degradation: TrendingDown,
  improvement: TrendingUp,
  stable: CheckCircle2,
  gap: AlertCircle,
};
const PATTERN_STYLE = {
  degradation: "text-red-600",
  improvement: "text-emerald-600",
  stable: "text-blue-600",
  gap: "text-amber-600",
};

const URGENCY_STYLE = {
  immediate: { bg: "bg-red-100", text: "text-red-700", label: "Immediat" },
  this_week: { bg: "bg-orange-100", text: "text-orange-700", label: "Aquesta setmana" },
  this_month: { bg: "bg-blue-100", text: "text-blue-700", label: "Aquest mes" },
};

function ScoreRing({ score, level }) {
  const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.medium;
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (score / 100) * circumference;
  return (
    <div className="relative w-36 h-36 mx-auto" data-testid="risk-score-ring">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="10" />
        <circle cx="60" cy="60" r={radius} fill="none" stroke="currentColor" strokeWidth="10"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dashOffset}
          className={cfg.color} style={{ transition: "stroke-dashoffset 1s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-black ${cfg.color}`}>{score}</span>
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">punts</span>
      </div>
    </div>
  );
}

function ModuleCard({ code, signal, weight }) {
  const meta = MODULE_META[code];
  if (!meta) return null;
  const Icon = meta.icon;
  const score = signal?.score ?? 0;
  const barColor = score >= 75 ? "bg-emerald-500" : score >= 50 ? "bg-amber-500" : "bg-red-500";
  return (
    <Link to={createPageUrl(meta.page)} className="block" data-testid={`module-card-${code}`}>
      <Card className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer group">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${meta.bg}`}>
            <Icon className={`w-4.5 h-4.5 ${meta.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{meta.label}</p>
            <p className="text-[10px] text-gray-400">Pes: {weight}%</p>
          </div>
          <span className={`text-lg font-black ${score >= 75 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-red-600"}`}>
            {score}
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full ${barColor} transition-all duration-700`} style={{ width: `${score}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between">
          <ModuleDetails code={code} signal={signal} />
          <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
        </div>
      </Card>
    </Link>
  );
}

function ModuleDetails({ code, signal }) {
  if (!signal) return null;
  const details = [];
  switch (code) {
    case "temperature":
      if (signal.total_30d > 0) details.push(`${signal.correct}/${signal.total_30d} correctes`);
      if (!signal.today_registered) details.push("Avui: pendent");
      break;
    case "cleaning":
      if (signal.today_pending > 0) details.push(`${signal.today_pending} pendent(s) avui`);
      break;
    case "pest_control":
      if (signal.open > 0) details.push(`${signal.open} oberta(es)`);
      if (signal.critical > 0) details.push(`${signal.critical} crític(a)`);
      break;
    case "maintenance":
      if (signal.overdue > 0) details.push(`${signal.overdue} endarrerit(s)`);
      break;
    case "traceability":
      if (signal.expired_batches > 0) details.push(`${signal.expired_batches} caducat(s)`);
      if (signal.active_recalls > 0) details.push(`${signal.active_recalls} retirada(es)`);
      break;
    case "incidents":
      if (signal.open > 0) details.push(`${signal.open} oberta(es)`);
      break;
    default: break;
  }
  if (details.length === 0) return <span className="text-[10px] text-emerald-600">Tot correcte</span>;
  return <span className="text-[10px] text-gray-500">{details.join(" · ")}</span>;
}

function AIInsightsPanel({ venueId }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState(null);

  const loadInsights = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/risk/ai-insights/${venueId}`);
      setInsights(res);
    } catch (e) {
      setError("No s'han pogut generar les insights IA.");
    }
    setLoading(false);
  }, [venueId]);

  if (!insights && !loading && !error) {
    return (
      <Card className="p-5 border border-[#7B2D3F]/20 shadow-sm bg-gradient-to-r from-[#7B2D3F]/5 to-transparent" data-testid="ai-insights-trigger">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#7B2D3F]/10 flex items-center justify-center flex-shrink-0">
            <Brain className="w-6 h-6 text-[#7B2D3F]" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-gray-900">Anàlisi IA de Riscos</h3>
            <p className="text-xs text-gray-500 mt-0.5">Detecció de patrons, predicció de riscos i accions preventives basades en les teves dades.</p>
          </div>
          <Button size="sm" onClick={loadInsights} className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90 gap-1.5" data-testid="generate-ai-insights-btn">
            <Zap className="w-3.5 h-3.5" /> Analitzar
          </Button>
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="p-6 border-0 shadow-sm" data-testid="ai-insights-loading">
        <div className="flex items-center gap-3 justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#7B2D3F]" />
          <span className="text-sm text-gray-500">Analitzant tendències, detectant patrons i generant prediccions...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-4 border border-red-200 shadow-sm bg-red-50">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-600">{error}</span>
          <Button variant="outline" size="sm" onClick={loadInsights} className="ml-auto">Reintentar</Button>
        </div>
      </Card>
    );
  }

  const patterns = insights?.patterns || [];
  const predictions = insights?.predictions || [];
  const actions = insights?.actions || [];
  const summary = insights?.summary || "";

  return (
    <Card className="border border-[#7B2D3F]/20 shadow-sm overflow-hidden" data-testid="ai-insights-panel">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#7B2D3F]/5 to-transparent border-b border-[#7B2D3F]/10">
        <div className="flex items-center gap-3">
          <Brain className="w-5 h-5 text-[#7B2D3F]" />
          <h3 className="text-sm font-bold text-gray-900">Anàlisi IA de Riscos</h3>
          <Badge className="bg-[#7B2D3F]/10 text-[#7B2D3F] border-0 text-[10px]">GPT-5.2</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={loadInsights} className="h-7 px-2 text-gray-500 hover:text-[#7B2D3F]" data-testid="refresh-ai-insights-btn">
            <RefreshCw className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="h-7 px-2 text-gray-500">
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Summary */}
          {summary && (
            <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-3 border border-gray-100" data-testid="ai-summary">
              {summary}
            </p>
          )}

          {/* Patterns Detected */}
          {patterns.length > 0 && (
            <div data-testid="ai-patterns">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <TrendingUp className="w-3 h-3" /> Patrons detectats
              </h4>
              <div className="space-y-1.5">
                {patterns.map((p, i) => {
                  const PIcon = PATTERN_ICON[p.type] || AlertCircle;
                  const pStyle = PATTERN_STYLE[p.type] || "text-gray-600";
                  return (
                    <div key={`risk-item-${i}`} className="flex items-start gap-2 bg-white rounded-lg border border-gray-100 p-2.5">
                      <PIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${pStyle}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800">{p.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-gray-400 capitalize">{p.area}</span>
                          <Badge className={`text-[9px] border-0 ${
                            p.severity === 'critical' ? 'bg-red-100 text-red-700' :
                            p.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                            p.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{p.severity}</Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Predictions */}
          {predictions.length > 0 && (
            <div data-testid="ai-predictions">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Target className="w-3 h-3" /> Prediccions de risc
              </h4>
              <div className="space-y-1.5">
                {predictions.map((p, i) => (
                  <div key={`risk-item-${i}`} className="bg-white rounded-lg border border-gray-100 p-2.5">
                    <div className="flex items-start gap-2">
                      <CalendarDays className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-800">{p.risk}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            p.probability === 'high' ? 'bg-red-100 text-red-700' :
                            p.probability === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {p.probability === 'high' ? 'Alta' : p.probability === 'medium' ? 'Mitjana' : 'Baixa'} probabilitat
                          </span>
                          {p.timeframe && <span className="text-[10px] text-gray-400">{p.timeframe}</span>}
                        </div>
                        {p.impact && <p className="text-[10px] text-gray-500 mt-1">{p.impact}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Actions */}
          {actions.length > 0 && (
            <div data-testid="ai-actions">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Zap className="w-3 h-3" /> Accions preventives
              </h4>
              <div className="space-y-1.5">
                {actions.map((a, i) => {
                  const urgCfg = URGENCY_STYLE[a.urgency] || URGENCY_STYLE.this_month;
                  return (
                    <div key={`risk-item-${i}`} className="flex items-start gap-2.5 bg-white rounded-lg border border-gray-100 p-2.5">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${urgCfg.bg}`}>
                        <span className={`text-[10px] font-black ${urgCfg.text}`}>{a.priority || i + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800">{a.action}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${urgCfg.bg} ${urgCfg.text}`}>
                            {urgCfg.label}
                          </span>
                          {a.area && <span className="text-[10px] text-gray-400">{a.area}</span>}
                        </div>
                        {a.reason && <p className="text-[10px] text-gray-500 mt-1">{a.reason}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Generated timestamp */}
          {insights?.generated_at && (
            <p className="text-[10px] text-gray-400 text-center pt-1">
              Generat: {new Date(insights.generated_at).toLocaleString("ca-ES")}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

export default function RiskDashboard() {
  const { currentVenue } = useVenue();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await hosys.risk.getDashboard(currentVenue.id);
      setData(res);
    } catch (e) {
      console.error("Risk dashboard error:", e);
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => { if (currentVenue) load(); }, [currentVenue]);

  if (loading) return <QuickLoader />;
  if (!data) return (
    <div className="space-y-5">
      <PageHeader title="Tauler de Riscos" subtitle="Motor de compliment APPCC unificat" />
      <Card className="p-8 border-0 shadow-sm text-center">
        <Gauge className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No s'ha pogut carregar el tauler de riscos.</p>
      </Card>
    </div>
  );

  const level = LEVEL_CONFIG[data.risk_level] || LEVEL_CONFIG.medium;
  const critAlerts = data.cross_alerts?.filter(a => a.severity === "critical") || [];
  const highAlerts = data.cross_alerts?.filter(a => a.severity === "high") || [];
  const otherAlerts = data.cross_alerts?.filter(a => a.severity !== "critical" && a.severity !== "high") || [];

  return (
    <div className="space-y-5" data-testid="risk-dashboard-page">
      <div className="flex items-center justify-between">
        <PageHeader title="Tauler de Riscos" subtitle="Motor de compliment APPCC unificat" />
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing} className="gap-1.5" data-testid="refresh-risk-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Actualitzar
        </Button>
      </div>

      {/* Overall Score */}
      <Card className={`p-6 border ${level.border} ${level.bg} shadow-sm`} data-testid="overall-risk-card">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <ScoreRing score={data.overall_score} level={data.risk_level} />
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
              <Shield className={`w-5 h-5 ${level.color}`} />
              <h2 className={`text-xl font-black ${level.color}`}>{data.risk_label}</h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Puntuació global basada en {Object.keys(data.signals || {}).length} mòduls ponderats.
            </p>
            {data.calculated_at && (
              <p className="text-[10px] text-gray-400 mt-2">
                Calculat: {new Date(data.calculated_at).toLocaleString("ca-ES")}
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Critical Alerts */}
      {critAlerts.length > 0 && (
        <div className="space-y-2" data-testid="critical-alerts">
          {critAlerts.map((alert, i) => {
            const SevIcon = SEV_ICON[alert.severity] || AlertCircle;
            return (
              <div key={`risk-item-${i}`} className={`rounded-2xl border p-4 ${SEV_STYLE[alert.severity]}`}>
                <div className="flex items-start gap-3">
                  <SevIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-bold">{alert.message}</p>
                    <p className="text-xs mt-1 opacity-80">{alert.action}</p>
                    <div className="flex gap-1 mt-2">
                      {(alert.modules || []).map(m => (
                        <Badge key={m} className="bg-white/50 text-[10px] border-0">{MODULE_META[m]?.label || m}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AI Insights Panel */}
      <AIInsightsPanel venueId={currentVenue?.venue_id || currentVenue?.id} />

      {/* Module Breakdown */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gray-400" /> Detall per mòdul
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="module-cards">
          {Object.entries(data.signals || {}).map(([code, signal]) => (
            <ModuleCard key={code} code={code} signal={signal} weight={data.weights?.[code] || 0} />
          ))}
        </div>
      </div>

      {/* Other Alerts */}
      {(highAlerts.length > 0 || otherAlerts.length > 0) && (
        <div data-testid="other-alerts">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-gray-400" /> Alertes i recomanacions
          </h3>
          <div className="space-y-2">
            {[...highAlerts, ...otherAlerts].map((alert, i) => {
              const SevIcon = SEV_ICON[alert.severity] || AlertCircle;
              return (
                <Card key={`risk-item-${i}`} className={`p-3 border shadow-sm ${SEV_STYLE[alert.severity] || "bg-gray-50 border-gray-200 text-gray-700"}`}>
                  <div className="flex items-start gap-2.5">
                    <SevIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold">{alert.message}</p>
                      <p className="text-[10px] mt-0.5 opacity-75">{alert.action}</p>
                    </div>
                    <Badge className="text-[9px] border-0 bg-white/50 flex-shrink-0">
                      {alert.severity === "high" ? "Alt" : alert.severity === "medium" ? "Mitjà" : "Baix"}
                    </Badge>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
